import React, { FormEvent, useEffect, useId, useState } from 'react';
import './ReferenceBookSelector.css';

interface ReferenceBookSelectorProps {
  currentBook?: string;
  currentTemplateId?: string;
  storyStylePrompt?: string;
  visualStylePrompt?: string;
  busy?: boolean;
  disabled?: boolean;
  onAnalyze: (bookName: string) => void | Promise<void>;
  onClear: () => void | Promise<void>;
  onUseSeriesTemplate?: (templateId: 'tiny-little-fox-sequel-v1') => void | Promise<void>;
}

const ReferenceBookSelector: React.FC<ReferenceBookSelectorProps> = ({
  currentBook,
  currentTemplateId,
  storyStylePrompt,
  visualStylePrompt,
  busy = false,
  disabled = false,
  onAnalyze,
  onClear,
  onUseSeriesTemplate
}) => {
  const inputId = useId();
  const [bookName, setBookName] = useState(currentBook || '');

  useEffect(() => {
    setBookName(currentBook || '');
  }, [currentBook]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = bookName.trim();
    if (!normalized || busy || disabled) return;
    await onAnalyze(normalized);
  };

  const clear = async () => {
    if (busy || disabled) return;
    setBookName('');
    await onClear();
  };

  return (
    <section className="reference-book-selector" data-testid="reference-book-selector">
      <div className="reference-book-heading">
        <div>
          <span className="reference-book-eyebrow">Optional creative direction</span>
          <h4>Reference book</h4>
        </div>
        {currentBook && <span className="reference-book-status">Applied</span>}
      </div>
      <p>
        Name a children’s book to derive high-level pacing, tone, palette, and composition guidance.
        Quick Create will not copy its text or illustrations.
      </p>
      {onUseSeriesTemplate && (
        <div className="reference-series-template" data-testid="reference-series-template">
          <div>
            <span>Measured local preset · no analysis charge</span>
            <strong>Tiny Little Fox sequel</strong>
            <p>8.25 × 11 trim, 2–3 short story beats, pale paper, and contained spot art below the reading area.</p>
          </div>
          <button
            type="button"
            data-testid="reference-series-template-use"
            className={currentTemplateId === 'tiny-little-fox-sequel-v1' ? 'selected' : ''}
            disabled={busy || disabled || currentTemplateId === 'tiny-little-fox-sequel-v1'}
            onClick={() => void onUseSeriesTemplate('tiny-little-fox-sequel-v1')}
          >
            {currentTemplateId === 'tiny-little-fox-sequel-v1' ? 'Template applied' : 'Use sequel template'}
          </button>
        </div>
      )}
      <form className="reference-book-form" onSubmit={submit}>
        <label htmlFor={inputId}>Book title</label>
        <div>
          <input
            id={inputId}
            data-testid="reference-book-input"
            type="text"
            value={bookName}
            onChange={event => setBookName(event.target.value)}
            placeholder="For example: a favorite picture book"
            maxLength={200}
            disabled={busy || disabled}
          />
          <button
            type="submit"
            data-testid="reference-book-analyze"
            disabled={!bookName.trim() || busy || disabled}
          >
            {busy ? 'Analyzing…' : currentBook ? 'Update direction' : 'Analyze & use'}
          </button>
        </div>
      </form>
      {currentBook && (
        <div className="reference-book-current" data-testid="reference-book-current">
          <div>
            <span>Current reference</span>
            <strong>{currentBook}</strong>
          </div>
          <button type="button" onClick={() => void clear()} disabled={busy || disabled}>Clear</button>
        </div>
      )}
      {(storyStylePrompt || visualStylePrompt) && (
        <details className="reference-book-details">
          <summary>Review the generated direction</summary>
          {storyStylePrompt && <div><strong>Story</strong><p>{storyStylePrompt}</p></div>}
          {visualStylePrompt && <div><strong>Visual</strong><p>{visualStylePrompt}</p></div>}
        </details>
      )}
    </section>
  );
};

export default ReferenceBookSelector;
